import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Download, TrendingUp, Clock, Award } from 'lucide-react';
import TrustScoreBadge from '../components/TrustScoreBadge';

export default function StatisticsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    api.get('/api/analytics/overview', { params: { months } }).then((r) => setData(r.data)).catch(() => {});
  }, [months]);

  if (!data) return <div className="p-8 text-text-secondary text-sm">{t('common.loading')}</div>;

  const locale = t('locale.numberFormat') || 'de-AT';
  const fmtEur = (n) => `€ ${(n || 0).toLocaleString(locale)}`;

  const series = data.series || [];
  const last = series[series.length - 1] || {};
  const totalSaved = series.reduce((s, x) => s + (x.saved_revenue_eur || 0), 0);
  const avgFill = series.length ? Math.round(series.reduce((s, x) => s + x.fill_rate, 0) / series.length) : 0;
  const allMedians = series.map((s) => s.median_response_min).filter((x) => x > 0);
  const avgResponse = allMedians.length ? Math.round(allMedians.reduce((s, x) => s + x, 0) / allMedians.length) : 0;

  async function exportCsv() {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/export.csv?months=${months}`;
    const tok = localStorage.getItem('studioops_access_token');
    const r = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `studioops-stats-${months}m.csv`;
    a.click();
  }

  return (
    <div data-testid="statistics-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('statistics.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">{t('statistics.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="so-input py-2" data-testid="stats-range">
            <option value={3}>{t('statistics.range.3m')}</option>
            <option value={6}>{t('statistics.range.6m')}</option>
            <option value={12}>{t('statistics.range.12m')}</option>
          </select>
          <button onClick={exportCsv} className="so-btn so-btn-secondary" data-testid="stats-export-csv">
            <Download size={14}/> CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Stat label={t('statistics.kpi.savedRevenue')} value={fmtEur(totalSaved)} color="#16A34A" icon={TrendingUp}/>
        <Stat label={t('statistics.kpi.fillRate')} value={`${avgFill}%`} color="#2B7FD4"/>
        <Stat label={t('statistics.kpi.responseTime')} value={`${avgResponse} ${t('statistics.minShort')}`} color="#D97706" icon={Clock}/>
        <Stat label={t('statistics.kpi.forecastNextMonth')} value={fmtEur(data.projected_next_month_eur)} color="#9333EA"/>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="so-card p-5">
          <h2 className="text-sm font-semibold mb-3">{t('statistics.charts.appointmentsPerMonth')}</h2>
          <div style={{ width: '100%', minHeight: 240, height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip wrapperStyle={{ fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Bar dataKey="confirmed" fill="#16A34A" name={t('statistics.legend.confirmed')}/>
                <Bar dataKey="cancelled" fill="#DC2626" name={t('statistics.legend.cancelled')}/>
                <Bar dataKey="no_show" fill="#D97706" name={t('statistics.legend.noShow')}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="so-card p-5">
          <h2 className="text-sm font-semibold mb-3">{t('statistics.charts.savedRevenuePerMonth')}</h2>
          <div style={{ width: '100%', minHeight: 240, height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`}/>
                <Tooltip wrapperStyle={{ fontSize: 12 }} formatter={(v) => `€ ${v}`}/>
                <Line type="monotone" dataKey="saved_revenue_eur" stroke="#2B7FD4" strokeWidth={3} name={t('statistics.legend.revenue')}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="so-card mt-6 p-5">
        <h2 className="text-sm font-semibold mb-3 flex items-center gap-2"><Award size={14} className="text-accent"/>{t('statistics.topPerformersTitle', { count: data.top_performers?.length || 0 })}</h2>
        {(!data.top_performers || data.top_performers.length === 0) ? (
          <p className="text-text-secondary text-sm">{t('statistics.empty')}</p>
        ) : (
          <ul className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="stats-top-performers">
            {data.top_performers.map((p, i) => (
              <li key={i} className="rounded-md border border-border p-3 text-center">
                <div className="text-xs text-text-secondary">#{i + 1}</div>
                <p className="font-semibold text-sm truncate">{p.first_name} {p.last_name}</p>
                <div className="mt-2 flex justify-center">
                  <TrustScoreBadge score={p.trust_score}/>
                </div>
                <p className="text-[11px] text-text-secondary mt-1">{p.responses} {t('statistics.answers')}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color, icon: Icon }) {
  return (
    <div className="so-card p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide text-text-secondary font-semibold">{label}</p>
        {Icon && <Icon size={14} style={{ color }}/>}
      </div>
      <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
    </div>
  );
}
